import { useEffect, useReducer } from "react";
import posthog from "posthog-js";
import type { PostHog } from "posthog-js";
import {
  useFeatureFlagPayload as usePostHogFeatureFlagPayload,
  usePostHog,
} from "posthog-js/react";
import { usePersonalizationCookieConsent } from "./oneTrustCookieConsent";

// Flags used strictly as a pseudo-CMS to get display content from PostHog.
// These flags should always be enabled for all users.
export const POSTHOG_PAYLOAD_FLAGS = {
  INFO_BANNER: "info-banner",
  INSTANCE_EXEC_LOG_EXCLUSIONS: "instance-exec-log-exclusions",
  LAUNCHABLE_MARKETPLACE_PLACEMENTS: "launchable-marketplace-placements",
} as const;

export type PostHogPayloadFlagKey =
  (typeof POSTHOG_PAYLOAD_FLAGS)[keyof typeof POSTHOG_PAYLOAD_FLAGS];

// Flags in this list must only be configured in PostHog with explicit identity
// targeting (email, org, user id). Do not add usage or inferred-persona release rules.
export const POSTHOG_IDENTITY_FLAGS = {
  ADMIN_NAVIGATION_REDESIGN: "admin-navigation-redesign",
  APPLICATION_SHELL_REDESIGN: "application-shell-redesign",
  APP_REDESIGN_EARLY_ACCESS: "app-redesign-early-access",
  APP_REDESIGN_EARLY_ACCESS_OPTION: "app-redesign-early-access-option",
  ADMIN_DASHBOARD_SUPER_ADMIN: "admin-dashboard-super-admin",
  ALLOW_API_KEYS: "allow-api-keys",
  BREV_SECRETS_UI: "brev-secrets-ui",
  ENVIRONMENT_LIST_APPLICATIONS_TAB: "environment-list-applications-tab",
  DGXC_ONLY: "dgxc-only",
  HIDE_FEATURED_CONTAINERS: "hide-featured-containers",
  GPU_PICKER_REDESIGN: "gpu-picker-redesign",
  INSTANCE_EXEC_LOG_HISTORY: "instance-exec-log-history",
  STARRED_COMPUTE: "starred-compute",
} as const;

export type PostHogIdentityFlagKey =
  (typeof POSTHOG_IDENTITY_FLAGS)[keyof typeof POSTHOG_IDENTITY_FLAGS];

// Flags in this list are gated by the personalization cookie consent.
// These include flags based on user usage or inferred-persona.
export const POSTHOG_PERSONALIZED_FLAGS = {} as const;

export type PostHogPersonalizedFlagKey =
  (typeof POSTHOG_PERSONALIZED_FLAGS)[keyof typeof POSTHOG_PERSONALIZED_FLAGS];

export const getPostHogFeatureFlagPayload = <TPayload>(
  flagKey: PostHogPayloadFlagKey,
): TPayload | undefined => {
  const payload = posthog.getFeatureFlagPayload(flagKey);
  return payload == null ? undefined : (payload as TPayload);
};

export const useFeatureFlagPayload = <TPayload>(
  flagKey: PostHogPayloadFlagKey,
): TPayload | null => {
  const payload = usePostHogFeatureFlagPayload(flagKey);

  return payload == null ? null : (payload as TPayload);
};

type PostHogIdentityResetClient = Pick<
  PostHog,
  "reset" | "resetPersonPropertiesForFlags"
>;

export const resetPostHogIdentity = (
  posthogClient: PostHogIdentityResetClient | null | undefined,
): void => {
  if (!posthogClient) return;

  posthogClient.resetPersonPropertiesForFlags();
  posthogClient.reset();
};

const useBooleanFeatureFlagEnabled = (
  flagKey: string,
  shouldEvaluateFlag: boolean,
  fallback = false,
): boolean | undefined => {
  const posthogClient = usePostHog();
  // PostHog updates its feature flag cache outside React; this forces a render
  // so the synchronous isFeatureEnabled read below sees the latest value.
  const [, forceFeatureFlagRender] = useReducer((version) => version + 1, 0);

  useEffect(() => {
    if (!shouldEvaluateFlag) {
      return;
    }

    return posthogClient.onFeatureFlags(() => {
      forceFeatureFlagRender();
    });
  }, [posthogClient, shouldEvaluateFlag]);

  if (!shouldEvaluateFlag) return fallback;

  const flagValue = posthogClient.isFeatureEnabled(flagKey);
  return typeof flagValue === "boolean" ? flagValue : undefined;
};

export const useIdentityFeatureFlagEnabled = (
  flagKey: PostHogIdentityFlagKey,
  fallback = false,
): boolean | undefined => {
  return useBooleanFeatureFlagEnabled(flagKey, true, fallback);
};

export const usePersonalizedFeatureFlagEnabled = (
  flagKey: PostHogPersonalizedFlagKey,
  fallback = false,
): boolean | undefined => {
  const hasPersonalizationCookieConsent = usePersonalizationCookieConsent();

  return useBooleanFeatureFlagEnabled(
    flagKey,
    hasPersonalizationCookieConsent,
    fallback,
  );
};
